import { get, isNil, every, some, isEmpty } from 'lodash';

import store from 'APP_ROOT/store';
import { getCurrentUser } from 'APP_ROOT/selectors/session';
import { hasFeatures, FEATURES } from 'APP_ROOT/utils/features';

// ---
// get feature flag to enable nested repeater conditional
const isConditionalEnabled = () => {
  const state = store.getState();
  const currentUser = getCurrentUser(state);
  const { featureFlags = [] } = currentUser;
  const enableConditional = hasFeatures(
    featureFlags,
    FEATURES.enableNestedRepeaterConditional
  );
  return enableConditional;
};

const isEmptyValue = value =>
  ['number', 'boolean'].includes(typeof value) ? false : isEmpty(value);

const valueIncluded = (conditionValue, valueToTest) => {
  const condIsArray = Array.isArray(conditionValue);
  const valueIsArray = Array.isArray(valueToTest);
  let included = false;
  if (condIsArray && valueIsArray) {
    included = conditionValue.some(cv => valueToTest.includes(cv));
  } else if (condIsArray) {
    included = conditionValue.includes(valueToTest);
  } else if (valueIsArray) {
    included = valueToTest.includes(conditionValue);
  } else {
    included =
      isEmptyValue(valueToTest) && isEmptyValue(conditionValue)
        ? true
        : valueToTest === conditionValue;
  }
  return included;
};

const validateBackendCondition = (condition, data, index) => {
  const { key = '' } = condition;
  const keyToTest = key.replace('.item.', `.${index}.`);
  const hasIncludes =
    Array.isArray(condition.includes) || typeof condition.includes === 'string';
  const hasExpected = !isNil(condition.expected);
  const valueToTest = get(data, keyToTest);
  let isExpected = true;
  let includesIt = true;
  if (hasExpected) {
    isExpected = valueIncluded(condition.expected, valueToTest);
  }
  const hasNotExpected = !isNil(condition.notExpected);
  if (hasNotExpected) {
    isExpected = !valueIncluded(condition.notExpected, valueToTest);
  }
  if (hasIncludes) {
    const valueToTestArray = valueToTest ? valueToTest : [];
    const validArrayValueToTest = Array.isArray(valueToTestArray)
      ? valueToTestArray
      : [valueToTestArray];
    const containsSingle = valueToTest
      ? valueToTest.includes(condition.includes)
      : false;
    includesIt = Array.isArray(condition.includes)
      ? validArrayValueToTest.some(e => condition.includes.includes(e))
      : containsSingle;
  }

  return isExpected && includesIt;
};

const validateRule = (fieldKey, rule, data, index, mainIndex) => {
  if (!rule) return true;

  const hasMustExist = rule.mustExist;
  const hasConditions = Array.isArray(rule.conditions);
  if (!hasConditions && !hasMustExist) return true;
  const { conditionType = 'and' } = rule;
  const assertMethod = conditionType === 'and' ? every : some; // shortcircuit evaluation of conditions: every=and, some=or
  let doesExist = true;
  let passesConditions = true;
  if (hasMustExist) {
    if (Array.isArray(rule.mustExist)) {
      doesExist = rule.mustExist
        .map(me => me.replace('.item.', `.${index}.`))
        .includes(fieldKey);
    } else {
      const k = rule.mustExist.replace('.item.', `.${index}.`);
      doesExist = k === fieldKey;
    }
  }
  if (hasConditions) {
    if (isConditionalEnabled()) {
      passesConditions = assertMethod(rule.conditions, cond => {
        // check if fieldKey and condition key are in the same
        // repeater, in that case use index instead of mainIndex
        // otherwise use mainIndex if not undefined
        const splittedKey = fieldKey.split('.');
        const condKey = cond.key.split('.');
        const condIndex =
          splittedKey[0] === condKey[0]
            ? index
            : mainIndex === undefined
            ? index
            : mainIndex;
        return validateBackendCondition(cond, data, condIndex);
      });
    } else {
      passesConditions = assertMethod(rule.conditions, cond =>
        validateBackendCondition(cond, data, index)
      );
    }
  }

  return passesConditions && doesExist;
};

const validateBackendConditions = (
  fieldKey,
  validationRules,
  data,
  index,
  mainIndex
) => {
  if (isConditionalEnabled()) {
    if (Array.isArray(validationRules)) {
      return validationRules.reduce(
        (acc, rule) =>
          acc && validateRule(fieldKey, rule, data, index, mainIndex),
        true
      );
    }
    return validateRule(fieldKey, validationRules, data, index, mainIndex);
  } else {
    if (Array.isArray(validationRules)) {
      return validationRules.reduce(
        (acc, rule) => acc && validateRule(fieldKey, rule, data, index),
        true
      );
    }
    return validateRule(fieldKey, validationRules, data, index);
  }
};

export default validateBackendConditions;
